import * as React from "react"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

/**
 * `pill` (default): the orange pill, for sub-tabs on a white card. `line` (spec-64-u2,
 * U-061): the main tab bar of a dossier or list, a white bar of its own with the active
 * tab in ink and a 2 px line in the action tint, the others muted, so it never sits
 * orange-on-orange on the gradient. Same look as the CRM `RecordTabs`.
 */
type TabsVariant = "pill" | "line"

const TabsVariantContext = React.createContext<TabsVariant>("pill")

const LIST_CLASS: Record<TabsVariant, string> = {
  pill: "flex flex-wrap gap-1.5",
  line: "flex gap-1 overflow-x-auto rounded-xl bg-card px-2 shadow-sm",
}

const TRIGGER_CLASS: Record<TabsVariant, string> = {
  pill:
    "rounded-full px-3.5 py-1.5 bg-white/80 hover:bg-white data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm [&_svg]:opacity-80 data-[state=active]:[&_svg]:opacity-100",
  line:
    // The bar scrolls sideways on a phone and clips what sticks out of it: the focus
    // outline is drawn inside the tab (as in RecordTabs).
    "border-b-2 border-transparent px-3 py-2.5 text-muted-foreground hover:text-foreground focus-visible:-outline-offset-2 data-[state=active]:border-primary data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:[&_svg]:text-primary",
}

function TabsList({
  className,
  variant = "pill",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.List
        data-slot="tabs-list"
        data-variant={variant}
        className={cn(LIST_CLASS[variant], className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  )
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap max-md:min-h-11 text-sm font-medium text-foreground transition-colors [&_svg]:size-4 [&_svg]:shrink-0",
        TRIGGER_CLASS[variant],
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={className}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
